import type { BlockStackLayoutConfig } from '@/lib/blocks/layout/block-stack-layout';
import type { GroupDirection } from '@/lib/blocks/layout/group-layout';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import type {
  ActiveDragData,
  DropIndicator,
  DropParentDirection,
  GapZoneData,
  ParentDropData,
} from '../core';
import type { DropValidationEventInput, EventOverSnapshot } from './event-adapters';
import { getEventActiveData, getEventActiveId, getEventOver } from './event-adapters';

export type DropOverData = ActiveDragData | ParentDropData | GapZoneData | undefined;

export interface DropResolutionContext {
  event: DropValidationEventInput;
  activeId: string;
  activeBlock: PageBlock;
  blockMap: Map<string, PageBlock>;
  stableIdByPersistedId: Map<string, string>;
  activeParent: PageBlock | undefined;
  activeDragType: 'block';
  activeParentBlockId: string | null;
  over: NonNullable<EventOverSnapshot>;
  overData: DropOverData;
  overBlock: PageBlock | undefined;
  effectiveOverId: string;
  activeParentId: string | null;
  targetParentBlockId: string | null;
  targetParentId: string | null;
  targetParent: PageBlock | undefined;
  targetParentDirection: DropParentDirection | null;
  getStableParentId: (
    block: PageBlock,
    stableIdByPersistedId: Map<string, string>,
  ) => string | null;
  rootParentDirection: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
  indicatorEdge: DropIndicator['edge'] | null;
  intentDirection: GroupDirection | null;
}

export const buildDropResolutionContext = ({
  event,
  blockMap,
  dropIndicator,
  fallbackOver,
  rootParentDirection,
  rootStackLayout,
  isGapZoneData,
  isParentDropData,
  getStableParentId,
  getParentDirection,
  getIntentDirectionFromEdge,
  getDragIntentDirection,
  resolveCanonicalDropIndicator,
}: {
  event: DropValidationEventInput;
  blockMap: Map<string, PageBlock>;
  dropIndicator: DropIndicator | null;
  fallbackOver?: EventOverSnapshot | null;
  rootParentDirection: GroupDirection;
  rootStackLayout?: BlockStackLayoutConfig;
  isGapZoneData: (data: unknown) => data is GapZoneData;
  isParentDropData: (data: unknown) => data is ParentDropData;
  getStableParentId: (
    block: PageBlock,
    stableIdByPersistedId: Map<string, string>,
  ) => string | null;
  getParentDirection: (
    block: PageBlock | undefined,
    blocks: PageBlock[],
    incomingBlock?: PageBlock,
  ) => DropParentDirection | null;
  getIntentDirectionFromEdge: (edge: DropIndicator['edge'] | null) => GroupDirection | null;
  getDragIntentDirection: (event: DropValidationEventInput) => GroupDirection | null;
  resolveCanonicalDropIndicator: (args: {
    event: DropValidationEventInput;
    over: NonNullable<EventOverSnapshot>;
    blockMap: Map<string, PageBlock>;
    stableIdByPersistedId: Map<string, string>;
    allBlocks: PageBlock[];
  }) => DropIndicator | null;
}): DropResolutionContext | null => {
  const stableIdByPersistedId = new Map(
    Array.from(blockMap.values()).map((block) => [block.id, getBlockUiKey(block)]),
  );
  const activeId = getEventActiveId(event);
  const over = fallbackOver ?? getEventOver(event);
  if (!over) {
    return null;
  }

  const activeBlock = blockMap.get(activeId);
  if (!activeBlock) {
    return null;
  }

  const overId = String(over.id);
  const activeData = getEventActiveData<ActiveDragData>(event);
  const overData = over.data.current as DropOverData;
  const canonicalIndicator =
    dropIndicator ??
    resolveCanonicalDropIndicator({
      event,
      over,
      blockMap,
      stableIdByPersistedId,
      allBlocks: Array.from(blockMap.values()),
    });
  const indicatorTargetId = canonicalIndicator?.targetId;
  const effectiveOverId = isGapZoneData(overData)
    ? indicatorTargetId && blockMap.has(indicatorTargetId)
      ? indicatorTargetId
      : overData.blockId
    : overId;
  const overBlock = blockMap.get(effectiveOverId);
  const activeParentId =
    activeData?.parentId ?? getStableParentId(activeBlock, stableIdByPersistedId);
  const activeParent = activeParentId ? blockMap.get(activeParentId) : undefined;
  const activeDragType = 'block' as const;
  const activeParentBlockId = activeParent?.id ?? activeBlock.parentBlockId ?? null;
  const targetParentId = isParentDropData(overData)
    ? overData.parentId
    : isGapZoneData(overData)
      ? overData.parentId
      : (overData?.parentId ??
        (overBlock ? getStableParentId(overBlock, stableIdByPersistedId) : null));
  const targetParent = targetParentId ? blockMap.get(targetParentId) : undefined;
  const targetParentBlockId = targetParent?.id ?? null;
  const targetParentDirection =
    getParentDirection(targetParent, Array.from(blockMap.values()), activeBlock) ??
    (targetParentId === null ? rootParentDirection : null);
  const indicatorEdge =
    canonicalIndicator?.targetId === effectiveOverId
      ? canonicalIndicator.edge
      : isGapZoneData(overData)
        ? overData.edge
        : null;
  const intentDirection =
    getIntentDirectionFromEdge(indicatorEdge) || getDragIntentDirection(event);

  return {
    event,
    activeId,
    activeBlock,
    blockMap,
    stableIdByPersistedId,
    activeParent,
    activeDragType,
    activeParentBlockId,
    over,
    overData,
    overBlock,
    effectiveOverId,
    activeParentId,
    targetParentBlockId,
    targetParentId,
    targetParent,
    targetParentDirection,
    getStableParentId,
    rootParentDirection,
    rootStackLayout,
    indicatorEdge,
    intentDirection,
  };
};
